/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/*
  Class Description

  This class is the look angles container. It manages the Look Angles Graphic, the telemetry/activity labels (heading,
  pitch, roll, beam, azimuth, elevation), the Satellites in FOV panel
 */

import React from 'react';
import { TerminalDetailsContext } from './LookAnglesContainerWrapper';
import { TerminalDetails } from '../../../../shared/entities/terminal';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../utils/StringUtils';

import LookAnglesCompass from './LookAnglesGraphic/LookAnglesCompass';
import { TerminalPlatformsEnum } from '../../../../shared/entities/terminalPlatform';
import {
  isPlatformTypeInList,
  valuePlaceholderOrNAForUnsupportedPlatformTypes,
} from '../../../../utils/PlatformTypeUtils';
import { HighlightedSatelliteInFov } from '../TabbedPanel/TerminalTabPanel';

export const DECIMAL_PLACES = 2;
export const BEAM_IN_USE_TOOLTIP_MAX_WIDTH_PX = 253;
export interface LookAnglesContainerProps {
  isOverviewMode: boolean;
  satellitesInFOVIndexOffset: number;
  highlightedSatelliteInFov: HighlightedSatelliteInFov | undefined;
  setHighlightedSatelliteInFov: Function;
}

function LookAnglesContainer({
  isOverviewMode,
  satellitesInFOVIndexOffset,
  highlightedSatelliteInFov,
  setHighlightedSatelliteInFov,
}: LookAnglesContainerProps) {
  const unknownPlaceholder = 'Unknown';
  const notApplicablePlaceholder = 'N/A';

  return (
    <TerminalDetailsContext.Consumer>
      {(terminalDetails: TerminalDetails) => (
        <div>
          {isPlatformTypeInList(terminalDetails.platformType, TerminalPlatformsEnum.Airborne) ? (
            <div className="look__angles__header__metrics">
              <span className="look__angles__metric__container heading">
                <div className="header">Heading</div>
                {valuePlaceholderOrNAForUnsupportedPlatformTypes(
                  terminalDetails.headingDeg,
                  unknownPlaceholder,
                  notApplicablePlaceholder,
                  terminalDetails.platformType,
                  val => `${val.toFixed(DECIMAL_PLACES)}°`,
                  TerminalPlatformsEnum.GroundNomadic,
                )}
              </span>
              <span className="look__angles__metric__container roll">
                <div className="header">Roll</div>
                {valuePlaceholderOrNAForUnsupportedPlatformTypes(
                  terminalDetails.rollAngleDeg,
                  EMPTY_FIELD_STRING_PLACEHOLDER,
                  notApplicablePlaceholder,
                  terminalDetails.platformType,
                  val => `${val.toFixed(DECIMAL_PLACES)}°`,
                  TerminalPlatformsEnum.GroundNomadic,
                )}
              </span>
              <span className="look__angles__metric__container pitch">
                <div className="header">Pitch</div>
                {valuePlaceholderOrNAForUnsupportedPlatformTypes(
                  terminalDetails.pitchAngleDeg,
                  EMPTY_FIELD_STRING_PLACEHOLDER,
                  notApplicablePlaceholder,
                  terminalDetails.platformType,
                  val => `${val.toFixed(DECIMAL_PLACES)}°`,
                  TerminalPlatformsEnum.GroundNomadic,
                )}
              </span>
            </div>
          ) : (
            <></>
          )}
          <LookAnglesCompass
            satellitesInFOVIndexOffset={satellitesInFOVIndexOffset}
            isOverviewMode={isOverviewMode}
            terminalDetails={terminalDetails}
            highlightedSatelliteInFov={highlightedSatelliteInFov}
            setHighlightedSatelliteInFov={setHighlightedSatelliteInFov}
          />
        </div>
      )}
    </TerminalDetailsContext.Consumer>
  );
}

export default LookAnglesContainer;
